import React from 'react';
import { Share2, EyeOff, ArrowUpDown, ArrowUp, ArrowDown, Settings } from 'lucide-react';
import { calculateNetStock, calculateDOI, formatNumber } from '../utils/calculations';
import { getEnrichedProduct } from '../utils/productEnrichment';

const InventoryTable = ({
    items,
    forecastFactor = 0,
    onRowClick,
    onToggleExclusion,
    isExcludedMode,
    selectedItems = new Set(),
    onSelectionChange,
    requestSort,
    sortConfig = { key: null, direction: 'asc' },
    hasOverride = () => false // פונקציה לבדיקה אם יש דריסה לפריט
}) => {

    const handleWhatsAppClick = (e, item) => {
        e.stopPropagation();
        const text = `*סטטוס פריט AEG:*
מק"ט: ${item.SKU}
מלאי פיזי: ${item.PhysicalStock}
נמל (הזמנות): ${item.OnOrder}
מכירות: ${formatNumber(item.Velocity)}

*סטטוס*: ${item.PhysicalStock <= 0 ? 'חסר' : 'קיים'}
`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const handleExcludeClick = (e, sku) => {
        e.stopPropagation();
        onToggleExclusion && onToggleExclusion(sku);
    };

    // Header Select All Toggle
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            onSelectionChange(new Set(items.map(i => i.SKU)));
        } else {
            onSelectionChange(new Set());
        }
    };

    // Single Row Toggle
    const handleSelectRow = (sku, checked) => {
        const newSelected = new Set(selectedItems);
        if (checked) {
            newSelected.add(sku);
        } else {
            newSelected.delete(sku);
        }
        onSelectionChange(newSelected);
    };

    const isAllSelected = items.length > 0 && selectedItems.size === items.length;
    const isIndeterminate = selectedItems.size > 0 && selectedItems.size < items.length;

    const getClassNamesFor = (name) => {
        if (!sortConfig.key || sortConfig.key !== name) {
            return 'text-gray-500 hover:text-gray-700'; // Default
        }
        return 'text-[#E30613] font-black'; // Active
    };

    const renderSortIcon = (name) => {
        if (sortConfig.key !== name) return <ArrowUpDown size={14} className="opacity-30" />;
        return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    };

    const SortableHeader = ({ label, sortKey, align = 'center', width }) => (
        <th
            className={`px-4 py-3.5 bg-gray-50/80 cursor-pointer select-none group transition-colors hover:bg-gray-100 ${width}`}
            onClick={() => requestSort && requestSort(sortKey)}
        >
            <div className={`flex items-center gap-1.5 ${align === 'right' ? 'justify-start' : 'justify-center'} ${getClassNamesFor(sortKey)}`}>
                <span>{label}</span>
                {renderSortIcon(sortKey)}
            </div>
        </th>
    );

    return (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col h-[600px]">
            <div className="overflow-auto flex-1">
                <table className="w-full text-left text-sm text-gray-600 relative">
                    <thead className="bg-gray-50/80 text-xs font-semibold tracking-wide text-gray-500 uppercase border-b border-gray-100 sticky top-0 z-10">
                        <tr>
                            <th className="px-4 py-3.5 w-12 bg-gray-50/80 text-center">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded border-gray-300 text-[#E30613] focus:ring-[#E30613] cursor-pointer"
                                    checked={isAllSelected}
                                    ref={el => el && (el.indeterminate = isIndeterminate)}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <SortableHeader label="מק&quot;ט" sortKey="SKU" align="right" width="w-32" />
                            <SortableHeader label="מלאי נטו" sortKey="netStock" width="w-24" />
                            <SortableHeader label="DOI" sortKey="doi" width="w-24" />
                            <SortableHeader label="סיווג" sortKey="Classification" width="w-24" />
                            <SortableHeader label="סטטוס" sortKey="netStock" width="w-24" /> {/* Status correlates to netStock */}
                            <th className="p-4 text-center w-32 bg-gray-50">פעולות</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {items.map((item) => {
                            const netStock = calculateNetStock(item);
                            const doi = calculateDOI(netStock, item.AvgDailySales, forecastFactor);
                            const status = netStock <= 0 ? 'red' : netStock < 30 ? 'yellow' : 'green';
                            const isSelected = selectedItems.has(item.SKU);

                            return (
                                <tr
                                    key={item.SKU}
                                    onClick={() => onRowClick && onRowClick(item)}
                                    className={`hover:bg-gray-50 transition-colors group cursor-pointer border-b last:border-0 border-gray-100 ${isSelected ? 'bg-red-50 hover:bg-red-50' : ''}`}
                                >
                                    <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-gray-300 text-[#E30613] focus:ring-[#E30613] cursor-pointer"
                                            checked={isSelected}
                                            onChange={(e) => handleSelectRow(item.SKU, e.target.checked)}
                                        />
                                    </td>
                                    <td className="p-4 font-mono text-gray-900 font-bold">
                                        <div className="flex items-center gap-1.5">
                                            <span>{item.SKU}</span>
                                            {hasOverride(item.SKU) && (
                                                <span
                                                    className="text-amber-500"
                                                    title="יש דריסת תחזית פעילה"
                                                >
                                                    <Settings size={14} />
                                                </span>
                                            )}
                                        </div>
                                        {(() => {
                                            const enriched = getEnrichedProduct(item.SKU);
                                            return enriched?.title ? (
                                                <p className="text-xs font-normal text-gray-500 mt-0.5 truncate max-w-[200px]" title={enriched.title}>
                                                    {enriched.title}
                                                </p>
                                            ) : null;
                                        })()}
                                    </td>

                                    <td className={`p-4 text-center font-bold ${netStock <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {formatNumber(netStock)}
                                    </td>
                                    <td className="p-4 text-center font-mono text-blue-600">{doi > 999 ? '∞' : doi}</td>

                                    <td className="px-4 py-3.5 text-center">
                                        <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold
                                            ${item.Classification === 'A' ? 'bg-violet-50 text-violet-700 border border-violet-100' :
                                                item.Classification === 'B' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                                                    'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                            {item.Classification}
                                        </span>
                                    </td>

                                    <td className="p-4 text-center">
                                        <div className={`w-3 h-3 rounded-full mx-auto shadow-[0_0_10px_currentColor] 
                                            ${status === 'red' ? 'bg-red-500 text-red-500' :
                                                status === 'yellow' ? 'bg-yellow-500 text-yellow-500' :
                                                    'bg-green-500 text-green-500'}`}
                                        ></div>
                                    </td>

                                    <td className="p-4 text-center">
                                        <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => handleWhatsAppClick(e, item)}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded-full transition"
                                                title="שתף בוואטסאפ"
                                            >
                                                <Share2 size={16} />
                                            </button>
                                            <button
                                                onClick={(e) => handleExcludeClick(e, item.SKU)}
                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
                                                title={isExcludedMode ? "החזר למלאי" : "החרג מק\"ט"}
                                            >
                                                <EyeOff size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default InventoryTable;
